import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getSelection, $isRangeSelection, FORMAT_TEXT_COMMAND } from 'lexical';
import {
  $setBlocksType,
  $patchStyleText,
  $getSelectionStyleValueForProperty,
} from '@lexical/selection';
import { $createHeadingNode, $createQuoteNode } from '@lexical/rich-text';
import { $createParagraphNode } from 'lexical';
import {
  INSERT_UNORDERED_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
} from '@lexical/list';
import { useCallback, useState, useEffect, useRef } from 'react';
import { T, useGT } from 'gt-react';
import {
  TextBIcon,
  TextItalicIcon,
  ListBulletsIcon,
  ListNumbersIcon,
  QuotesIcon,
  TextTIcon,
  ImageIcon,
  SpinnerIcon,
  TextAaIcon,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { INSERT_IMAGE_COMMAND } from '@/components/common/lexical-image/ImageNode';
import { useToast } from '@/context/ToastContext';

// Curated text-color palette. `value: null` clears the color back to default.
const TEXT_COLORS: { value: string | null; labelKey: string }[] = [
  { value: null, labelKey: 'Default' },
  { value: '#000000', labelKey: 'Black' },
  { value: '#5c5c5c', labelKey: 'Gray' },
  { value: '#b91c1c', labelKey: 'Red' },
  { value: '#c2410c', labelKey: 'Orange' },
  { value: '#a16207', labelKey: 'Yellow' },
  { value: '#15803d', labelKey: 'Green' },
  { value: '#0e7490', labelKey: 'Teal' },
  { value: '#1d4ed8', labelKey: 'Blue' },
  { value: '#6d28d9', labelKey: 'Purple' },
  { value: '#be185d', labelKey: 'Pink' },
  { value: '#ffffff', labelKey: 'White' },
];

export interface ToolbarPluginProps {
  /**
   * Enables an "insert image" button. Receives the picked file, uploads it,
   * and returns the rendered `src` plus the durable `documentId`. When omitted,
   * the image button is hidden.
   */
  uploadImage?: (file: File) => Promise<{ src: string; documentId: string }>;
}

export function ToolbarPlugin({ uploadImage }: ToolbarPluginProps = {}) {
  const [editor] = useLexicalComposerContext();
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [currentColor, setCurrentColor] = useState('');
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const gt = useGT();
  const { showToast } = useToast();

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setCurrentColor(
        $getSelectionStyleValueForProperty(selection, 'color', ''),
      );
    }
  }, []);

  // Apply (or clear, when `color` is null) the text color over the selection.
  const applyTextColor = (color: string | null) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $patchStyleText(selection, { color });
      }
    });
  };

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        updateToolbar();
      });
    });
  }, [editor, updateToolbar]);

  const formatBold = () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
  };

  const formatItalic = () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
  };

  const formatHeading = (headingSize: 'h1' | 'h2' | 'h3') => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createHeadingNode(headingSize));
      }
    });
  };

  const formatParagraph = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createParagraphNode());
      }
    });
  };

  const formatQuote = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createQuoteNode());
      }
    });
  };

  const formatBulletList = () => {
    editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
  };

  const formatNumberedList = () => {
    editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
  };

  const handleImageFileSelected = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    // Reset so picking the same file again re-triggers onChange.
    event.target.value = '';
    if (!file || !uploadImage) return;
    setIsUploadingImage(true);
    try {
      const { src, documentId } = await uploadImage(file);
      editor.dispatchCommand(INSERT_IMAGE_COMMAND, { src, documentId });
    } catch (error) {
      console.error('Signature image upload failed:', error);
      showToast({
        title: gt('Image upload failed', { context: 'toast title' }),
        message:
          error instanceof Error
            ? error.message
            : gt('Could not upload the image. Please try again.', {
                context: 'toast message',
              }),
        color: 'error',
        icon: 'x',
      });
    } finally {
      setIsUploadingImage(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-1 p-2 border-b border-gray-200 bg-gray-50">
      <Button
        variant="ghost"
        size="sm"
        onClick={formatBold}
        className={cn('h-8 w-8 p-0', isBold && 'bg-gray-200')}
        title={gt('Bold', {
          context: 'toolbar button title for bold text formatting',
        })}
      >
        <TextBIcon className="h-4 w-4" />
      </Button>

      <Button
        variant="ghost"
        size="sm"
        onClick={formatItalic}
        className={cn('h-8 w-8 p-0', isItalic && 'bg-gray-200')}
        title={gt('Italic', {
          context: 'toolbar button title for italic text formatting',
        })}
      >
        <TextItalicIcon className="h-4 w-4" />
      </Button>

      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 flex flex-col items-center justify-center gap-0.5"
            title={gt('Text color', {
              context: 'toolbar button title for text color',
            })}
          >
            <TextAaIcon className="h-4 w-4" />
            <span
              className="block h-1 w-4 rounded-sm border border-gray-300"
              style={{ backgroundColor: currentColor || '#000000' }}
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-2" align="start">
          <div className="grid grid-cols-6 gap-1">
            {TEXT_COLORS.map((color) => {
              const isActive =
                (color.value ?? '').toLowerCase() ===
                currentColor.toLowerCase();
              return (
                <button
                  key={color.labelKey}
                  type="button"
                  onClick={() => applyTextColor(color.value)}
                  title={color.labelKey}
                  className={cn(
                    'flex h-6 w-6 items-center justify-center rounded border border-gray-300',
                    isActive && 'ring-2 ring-blue-500 ring-offset-1',
                  )}
                  style={
                    color.value ? { backgroundColor: color.value } : undefined
                  }
                >
                  {color.value === null && (
                    <span className="text-[10px] font-medium text-gray-600">
                      A
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <label className="mt-2 flex cursor-pointer items-center gap-2 text-xs text-gray-600">
            <input
              type="color"
              value={currentColor.startsWith('#') ? currentColor : '#000000'}
              onChange={(event) => applyTextColor(event.target.value)}
              className="h-6 w-6 cursor-pointer border-0 bg-transparent p-0"
            />
            <T context="custom text color option label">Custom…</T>
          </label>
        </PopoverContent>
      </Popover>

      <div className="w-px h-6 bg-gray-300 mx-1" />

      <Button
        variant="ghost"
        size="sm"
        onClick={formatParagraph}
        className="h-8 px-2 text-xs"
        title={gt('Paragraph', {
          context: 'toolbar button title for paragraph text formatting',
        })}
      >
        <TextTIcon className="h-4 w-4 mr-1" />P
      </Button>

      <Button
        variant="ghost"
        size="sm"
        onClick={() => formatHeading('h1')}
        className="h-8 px-2 text-xs font-bold"
        title={gt('Heading 1', {
          context: 'toolbar button title for heading 1 text formatting',
        })}
      >
        <T context="toolbar button label for heading 1">H1</T>
      </Button>

      <Button
        variant="ghost"
        size="sm"
        onClick={() => formatHeading('h2')}
        className="h-8 px-2 text-xs font-bold"
        title={gt('Heading 2', {
          context: 'toolbar button title for heading 2 text formatting',
        })}
      >
        <T context="toolbar button label for heading 2">H2</T>
      </Button>

      <Button
        variant="ghost"
        size="sm"
        onClick={() => formatHeading('h3')}
        className="h-8 px-2 text-xs font-bold"
        title={gt('Heading 3', {
          context: 'toolbar button title for heading 3 text formatting',
        })}
      >
        <T context="toolbar button label for heading 3">H3</T>
      </Button>

      <div className="w-px h-6 bg-gray-300 mx-1" />

      <Button
        variant="ghost"
        size="sm"
        onClick={formatBulletList}
        className="h-8 w-8 p-0"
        title={gt('Bullet List', {
          context: 'toolbar button title for bullet list formatting',
        })}
      >
        <ListBulletsIcon className="h-4 w-4" />
      </Button>

      <Button
        variant="ghost"
        size="sm"
        onClick={formatNumberedList}
        className="h-8 w-8 p-0"
        title={gt('Numbered List', {
          context: 'toolbar button title for numbered list formatting',
        })}
      >
        <ListNumbersIcon className="h-4 w-4" />
      </Button>

      <Button
        variant="ghost"
        size="sm"
        onClick={formatQuote}
        className="h-8 w-8 p-0"
        title={gt('Quote', {
          context: 'toolbar button title for blockquote formatting',
        })}
      >
        <QuotesIcon className="h-4 w-4" />
      </Button>

      {uploadImage && (
        <>
          <div className="w-px h-6 bg-gray-300 mx-1" />
          <Button
            variant="ghost"
            size="sm"
            disabled={isUploadingImage}
            onClick={() => fileInputRef.current?.click()}
            className="h-8 w-8 p-0"
            title={gt('Insert Image', {
              context: 'toolbar button title for inserting an image',
            })}
          >
            {isUploadingImage ? (
              <SpinnerIcon className="h-4 w-4 animate-spin" />
            ) : (
              <ImageIcon className="h-4 w-4" />
            )}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp"
            className="hidden"
            onChange={handleImageFileSelected}
          />
        </>
      )}
    </div>
  );
}
